import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Navigate, Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';

const SHOW_SALES_BY_PRICING_GROUP_FLAG = 'moniebook.pos.pricingGroups';
const ENABLE_DISCOUNTS_FLAG = 'moniebook.pos.discounts';
const LOYALTY_REPORTS_ENABLED_FLAG = 'moniebook.pos.loyaltyReportsEnabled';
const MODIFIER_REPORTS_ENABLED_FLAG = 'moniebook.pos.modifierReportsEnabled';

const REPORTS_PERMISSIONS = ['VIEW_REPORT'];

export const reportsRoutes: RouteObject[] = [
    {
        path: '',
        element: <StyleScope name='reports-ui' />,
        children: [
            {
                path: 'reports',
                element: <ProtectedView accessCodeList={REPORTS_PERMISSIONS} />,
                children: [
                    {
                        path: '',
                        element: <Navigate to='/dashboard/reports/summary' />,
                    },
                    {
                        path: 'summary',
                        async lazy() {
                            const { salesSummary } = await unwrapFeatures();
                            return salesSummary().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'sales-by-item',
                        async lazy() {
                            const { salesByItem } = await unwrapFeatures();
                            return salesByItem().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'sales-by-category',
                        async lazy() {
                            const { salesByCategories } =
                                await unwrapFeatures();
                            return salesByCategories().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'staff',
                        async lazy() {
                            const { salesByStaff } = await unwrapFeatures();
                            return salesByStaff().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'payment-method',
                        async lazy() {
                            const { salesByPaymentMethod } =
                                await unwrapFeatures();
                            return salesByPaymentMethod().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'tax',
                        async lazy() {
                            const { taxes } = await unwrapFeatures();
                            return taxes().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'pricing-group',
                        element: (
                            <FeatureProtectedView
                                flag={SHOW_SALES_BY_PRICING_GROUP_FLAG}>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { salesByPricingGroup } =
                                        await unwrapFeatures();
                                    return salesByPricingGroup().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'discounts',
                        element: (
                            <FeatureProtectedView flag={ENABLE_DISCOUNTS_FLAG}>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { salesByDiscounts } =
                                        await unwrapFeatures();
                                    return salesByDiscounts().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'customer',
                        async lazy() {
                            const { salesByCustomers } = await unwrapFeatures();
                            return salesByCustomers().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'loyalty',
                        element: (
                            <FeatureProtectedView
                                flag={LOYALTY_REPORTS_ENABLED_FLAG}
                                noAltView>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        async lazy() {
                            const { loyalty } = await unwrapFeatures();
                            return loyalty().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'modifiers',
                        element: (
                            <FeatureProtectedView
                                flag={MODIFIER_REPORTS_ENABLED_FLAG}
                                noAltView>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        async lazy() {
                            const { modifiers } = await unwrapFeatures();
                            return modifiers().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('reports/features');

    return features.default;
}
